import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from 'react';

import classNames from 'classnames';

import { Wrapper } from '../../components';
import {
    Progress,
    SectionFooter,
    Slider,
    SliderProps,
} from '../../compositions';
import { Device } from '../../entities/Device/Device';
import { SliderSectionInterface } from '../../entities/SliderSection/SliderSection';
import { useDeviceWidth } from '../../hooks';
import { SliderSectionSkeletons } from './subcomponents';

import './SliderSection.scss';

interface SliderSectionProps extends SliderSectionInterface, Pick<SliderProps, 'items' | 'columns'> {
    isLoading?: boolean;
    titleHasThemeColor?: boolean;
    startAtCenter?: boolean;
    className?: string;
    sliderClassName?: string;
}

export const SliderSection: FC<SliderSectionProps> = ({
    isLoading,
    titleHasThemeColor,
    startAtCenter,
    id,
    title,
    items,
    columns,
    link,
    className = '',
    sliderClassName = '',
}): ReactElement => {
    const { device } = useDeviceWidth();

    const [activeIndex, setActiveIndex] = useState<number>(0);

    const columnData = useMemo((): Record<Device, number> => ({
        [Device.mobile]: 1,
        [Device.tabletPortrait]: 2,
        [Device.tabletLandscape]: 3,
        [Device.desktop]: 4,
        ...columns,
    }), [columns]);

    useEffect((): void => {
        if (startAtCenter && device) {
            const offsetColumns = (items.length - columnData[device]) / 2;
            const indexToCenter = Math.floor(offsetColumns);

            setActiveIndex(indexToCenter);
        }
    }, [startAtCenter]);

    const maxIndex = useMemo((): number => {
        if (!device) {
            return items.length;
        }

        const deviceMaxIndex = items.length - (columnData[device] - 1);

        return Math.max(deviceMaxIndex, 1);
    }, [items, columnData, device]);

    if (isLoading) {
        return (
            <SliderSectionSkeletons />
        );
    }

    const sliderSectionTitleClassNames = classNames('slider-section__title', {
        'slider-section__title--has-theme-color': titleHasThemeColor,
    });

    return (
        <section className={`slider-section ${className}`}>
            {title && (
                <header className="slider-section__header">
                    <Wrapper className="slider-section__header-wrapper">
                        <h2 className={sliderSectionTitleClassNames}>
                            {title}
                        </h2>

                        <Progress
                            total={maxIndex}
                            activeIndex={activeIndex}
                            onChange={setActiveIndex}
                            className="slider-section__progress"
                        />
                    </Wrapper>
                </header>
            )}

            <Slider
                id={id}
                activeIndex={activeIndex}
                maxIndex={maxIndex}
                items={items}
                columns={columnData}
                onChange={setActiveIndex}
                className={`slider-section__slider ${sliderClassName}`}
            />

            {link && (
                <Wrapper className="slider-section__footer-wrapper">
                    <SectionFooter link={link} />
                </Wrapper>
            )}
        </section>
    );
};
